/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import clsx from 'clsx';
import isUndefined from 'lodash/isUndefined';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ICON_HELP_OUTLINE, ICON_TRENDING_DOWN, ICON_TRENDING_FLAT, ICON_TRENDING_UP } from '../../../../Icons';
import { Popover } from '../../../Popover';
import { SdsIcon } from '../../../SdsIcon';
import { DASHBOARD_SUMMARY_STRINGS } from '../../DashboardSummary.strings';
import { TREND_DOWN, TREND_FLAT, TREND_UP } from './DashboardSummaryItem.constants';
import { TrendDirection } from './DashboardSummaryItem.defs';
import { DashboardSummaryItemProps } from './DashboardSummaryItem.props';

/**
 * Get the trend direction.
 */
export const getTrendDirection = (current: number, before?: number): TrendDirection => {
  const diff = current - (before ?? 0);
  if (diff === 0 || before === 0 || !before) {
    return TREND_FLAT;
  }
  if (diff > 0) {
    return TREND_UP;
  }

  return TREND_DOWN;
};

/**
 * Get the difference between 2 numbers as a percentage value.
 */
export const getDiffAsPercentage = (current: number, before: number, unit?: string): number => {
  // Don't divide by zero.
  if (before === 0) {
    return 0;
  }

  if (unit === '%') {
    const pc = current - before;
    return Math.abs(pc);
  }

  const pc = ((current - before) / before) * 100;
  return Math.abs(pc);
};

/**
 * Format a number into a display string.
 */
export const formatNumber = (num: number): string => {
  return num
    .toFixed(2)
    .replace(/\d(?=(\d{3})+\.)/g, '$&,')
    .replace(`.00`, ``);
};

/**
 * Get the trend icon to display.
 */
export const getTrendIcon = (trend: TrendDirection): JSX.Element => {
  switch (trend) {
    case TREND_DOWN:
      return ICON_TRENDING_DOWN;
    case TREND_UP:
      return ICON_TRENDING_UP;
    default:
      return ICON_TRENDING_FLAT;
  }
};

/**
 * SDS Dashboard Summary.
 */
export const DashboardSummaryItem = (props: DashboardSummaryItemProps): JSX.Element => {
  const {
    count,
    countUnit,
    classes,
    displayEmpty,
    description,
    inverted = false,
    previousCount,
    timeValue,
    timePeriod,
    title,
    tooltip,
    hideTrend,
    ...rest
  } = props;
  const { t } = useTranslation();
  // Labels
  const labels = useMemo(() => {
    return {
      lastXDays: t(DASHBOARD_SUMMARY_STRINGS.lastXDays, { count: timeValue }),
      title: t(title),
      vsPreviousXDays: t(DASHBOARD_SUMMARY_STRINGS.vsPreviousXDays, { count: timeValue, period: timePeriod }),
    };
  }, [t, timePeriod, timeValue, title]);

  const trend = useMemo((): TrendDirection => {
    return getTrendDirection(count, previousCount);
  }, [count, previousCount]);

  const percentage = useMemo((): string => {
    if ((!previousCount && previousCount !== 0) || isNaN(previousCount)) {
      return `--%`;
    }
    return `${Math.round(getDiffAsPercentage(count, previousCount, countUnit))}%`;
  }, [count, countUnit, previousCount]);

  const icon = useMemo((): JSX.Element => {
    return getTrendIcon(trend);
  }, [trend]);

  return (
    <div className={clsx('dashboard-summary-item', classes)} {...rest}>
      <h4 className="dashboard-summary-item__title">
        {labels.title}
        {tooltip && (
          <Popover content={tooltip} title={labels.title}>
            <div className="dashboard-summary-item__help">
              <SdsIcon classes="dashboard-summary-item__help-icon" svg={ICON_HELP_OUTLINE} />
            </div>
          </Popover>
        )}
      </h4>
      <hr />
      <div className="dashboard-summary-item__count">
        {displayEmpty ? '--' : formatNumber(count)}
        {countUnit}
      </div>
      <div className="dashboard-summary-item__description">
        {isUndefined(description) ? labels.lastXDays : description}
      </div>
      {!hideTrend && (
        <div
          className={clsx(`dashboard-summary-item__trend`, `${trend}`, {
            inverted,
          })}
        >
          {previousCount ? <div className="dashboard-summary-item__trend-icon">{icon}</div> : <></>}
          <div className="dashboard-summary-item__trend-percentage">{percentage}</div>
          <div className="dashboard-summary-item__trend-description">{labels.vsPreviousXDays}</div>
        </div>
      )}
    </div>
  );
};
